import React, { useState, useEffect, useCallback } from 'react';
import { Globe, Plus, Edit, Trash2, Loader2, GripVertical, ToggleLeft, ToggleRight } from 'lucide-react';
import { useTheme } from '@framework/contexts/ThemeContext';
import BaseModal from '@framework/components/BaseModal';
import ActionButton from '@framework/components/ActionButton';
import ErrorAlert from '@framework/components/ErrorAlert';

const inputClass =
  'w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent';

const EMPTY_FORM = { name: '', url: '', enabled: true };

export default function RecipeSitesPage() {
  const { apiService: api } = useTheme();
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const [dragIdx, setDragIdx] = useState(null);
  const [reordering, setReordering] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getRecipeSites();
      setSites(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load sites');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  // ── Modal helpers ────────────────────────────────────────────────────────────

  function openCreate() {
    setEditingId(null);
    setForm({ ...EMPTY_FORM });
    setFormError(null);
    setModalOpen(true);
  }

  function openEdit(site) {
    setEditingId(site.id);
    setForm({ name: site.name, url: site.url, enabled: site.enabled });
    setFormError(null);
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setEditingId(null);
    setFormError(null);
  }

  async function handleSave() {
    if (!form.name.trim()) { setFormError('Name is required'); return; }
    if (!form.url.trim()) { setFormError('URL is required'); return; }
    try {
      new URL(form.url.trim());
    } catch {
      setFormError('URL must be a valid web address (include https://)');
      return;
    }

    try {
      setSaving(true);
      setFormError(null);
      const payload = {
        name: form.name.trim(),
        url: form.url.trim(),
        enabled: form.enabled,
        sort_order: editingId
          ? sites.find((s) => s.id === editingId)?.sort_order ?? 0
          : (sites.length > 0 ? Math.max(...sites.map((s) => s.sort_order)) + 1 : 0),
      };
      if (editingId) {
        await api.updateRecipeSite(editingId, payload);
      } else {
        await api.createRecipeSite(payload);
      }
      closeModal();
      load();
    } catch (err) {
      setFormError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(site) {
    if (!confirm(`Delete "${site.name}"?`)) return;
    try {
      await api.deleteRecipeSite(site.id);
      load();
    } catch (err) {
      alert(err.message || 'Delete failed');
    }
  }

  async function handleToggle(site) {
    try {
      await api.updateRecipeSite(site.id, { enabled: !site.enabled });
      setSites((prev) =>
        prev.map((s) => (s.id === site.id ? { ...s, enabled: !s.enabled } : s))
      );
    } catch (err) {
      alert(err.message || 'Update failed');
    }
  }

  // ── Drag-to-reorder ──────────────────────────────────────────────────────────

  function handleDragStart(e, idx) {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
  }

  function handleDragOver(e, idx) {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    const next = [...sites];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(idx, 0, moved);
    setDragIdx(idx);
    setSites(next);
  }

  async function handleDragEnd() {
    setDragIdx(null);
    try {
      setReordering(true);
      const items = sites.map((s, i) => ({ id: s.id, sort_order: i + 1 }));
      await api.reorderRecipeSites(items);
    } catch (err) {
      alert('Reorder failed: ' + (err.message || err));
      load();
    } finally {
      setReordering(false);
    }
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  const faviconUrl = (url) => {
    try {
      return `https://www.google.com/s2/favicons?domain=${new URL(url).host}&sz=32`;
    } catch {
      return null;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Globe className="w-6 h-6 text-orange-500" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Recipe Sites</h1>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {sites.filter((s) => s.enabled).length} / {sites.length} enabled
          </span>
        </div>
        <ActionButton onClick={openCreate} icon={Plus}>
          Add Site
        </ActionButton>
      </div>

      {error && <ErrorAlert message={error} onDismiss={() => setError(null)} />}

      <p className="text-sm text-gray-500 dark:text-gray-400">
        These sites appear as quick-pick chips in the app's recipe browser. Drag to reorder.
        Disabled sites are hidden from users but kept for reference.
      </p>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        </div>
      ) : (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          {sites.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              No sites yet. Add one to get started.
            </div>
          ) : (
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {sites.map((site, idx) => (
                <li
                  key={site.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, idx)}
                  onDragOver={(e) => handleDragOver(e, idx)}
                  onDragEnd={handleDragEnd}
                  className={`flex items-center gap-3 px-4 py-3 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors ${
                    dragIdx === idx ? 'opacity-50' : ''
                  } ${!site.enabled ? 'opacity-60' : ''}`}
                >
                  <GripVertical className="w-4 h-4 text-gray-400 cursor-grab flex-shrink-0" />

                  {faviconUrl(site.url) ? (
                    <img
                      src={faviconUrl(site.url)}
                      alt=""
                      className="w-5 h-5 flex-shrink-0"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <Globe className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}

                  <div className="flex-1 min-w-0">
                    <span className="font-medium text-gray-900 dark:text-white">{site.name}</span>
                    <span className="ml-2 text-xs text-gray-500 dark:text-gray-400 truncate">{site.url}</span>
                  </div>

                  <button
                    onClick={() => handleToggle(site)}
                    title={site.enabled ? 'Disable' : 'Enable'}
                    className="flex-shrink-0 text-gray-400 hover:text-orange-500 transition-colors"
                  >
                    {site.enabled
                      ? <ToggleRight className="w-5 h-5 text-orange-500" />
                      : <ToggleLeft className="w-5 h-5" />}
                  </button>

                  <button
                    onClick={() => openEdit(site)}
                    className="flex-shrink-0 text-gray-400 hover:text-blue-500 transition-colors"
                    title="Edit"
                  >
                    <Edit className="w-4 h-4" />
                  </button>

                  <button
                    onClick={() => handleDelete(site)}
                    className="flex-shrink-0 text-gray-400 hover:text-red-500 transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {reordering && (
        <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
          <Loader2 className="w-3 h-3 animate-spin" /> Saving order…
        </p>
      )}

      <BaseModal
        isOpen={modalOpen}
        onClose={closeModal}
        title={editingId ? 'Edit Site' : 'Add Site'}
      >
        <div className="space-y-4">
          {formError && <ErrorAlert message={formError} onDismiss={() => setFormError(null)} />}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Name
            </label>
            <input
              className={inputClass}
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Serious Eats"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              URL
            </label>
            <input
              className={inputClass}
              value={form.url}
              onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
              placeholder="https://www.seriouseats.com"
              type="url"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              id="site-enabled"
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
              className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
            />
            <label htmlFor="site-enabled" className="text-sm text-gray-700 dark:text-gray-300">
              Enabled (visible to users)
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={closeModal}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <ActionButton onClick={handleSave} loading={saving}>
              {editingId ? 'Save Changes' : 'Add Site'}
            </ActionButton>
          </div>
        </div>
      </BaseModal>
    </div>
  );
}
